import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSWRConfig } from "swr";
import { useEnvironment } from "../../Shared/components/EnvironmentProvider/EnvironmentProvider";
/**
 * Component to render a subtitle or maintain layout spacing if no subtitle is present
 * @param subtitle - Optional subtitle to display
 * @returns The subtitle if provided
 */
const SubtitleSection = ({ subtitle }) => {
    return _jsx(Typography, Object.assign({ variant: "body1" }, { children: subtitle }));
};
// Function to clear all cached SWR data and reload the page
export const clearSWRCache = (mutate) => () => {
    if (window.confirm("WARNING: This will clear all SWR cached data and reload the page! Are you sure?")) {
        // Clear the persistent SWR data from localStorage
        window.localStorage.removeItem("app-cache");
        // Clear all SWR managed cache entries
        mutate(() => true, // Match all keys
        undefined, // Set data to undefined
        { revalidate: false });
        window.location.reload(); // Reload the page
        console.log("THESE PIPES... ARE CLEAN! (cache busted)");
    }
    else {
        console.log("THESE PIPES... ARE NOT CLEAN! (cache not busted)");
    }
};
/**
 * Creates a hidden button to clear the SWR cache and reload the page when clicked.
 * @param title - Title of the page
 * @param environment - Environment of the application (e.g. localhost, production)
 * @returns
 */
const TitleComponent = ({ title, environment }) => {
    const { mutate } = useSWRConfig();
    // ! Uncomment the SWRInspector function to enable SWR cache inspection via a button in the UI
    if (environment === "localhost") {
        return (_jsx(Stack, Object.assign({ flexDirection: "row", alignItems: "flexStart", justifyContent: "flexStart", gap: 2 }, { children: _jsx(Typography, Object.assign({ variant: "h1", onClick: clearSWRCache(mutate) }, { children: title })) })));
    }
    return _jsx(Typography, Object.assign({ variant: "h1" }, { children: title }));
};
/**
 * Custom component to keep the titles consistent on different pages of the Account section
 * @note This component renders the title, subtitle, and children in the following order:
 * * For mobile view, a column layout is used.
 * * Row 1. Title
 * * Row 2. Subtitle
 * * Row 3. Children
 *
 * * For desktop view, a mixed column/row layout is used.
 * * Row 1 (left) Title, (right) Children
 * * Row 2. Subtitle
 *
 * * The effect of this layout is that the subtitle is always displayed below the title.
 *
 * @param title - Title of the page
 * @param children - Component to display next to the title. (e.g. buttons, selects, etc.)
 * @returns
 */
export const AccountPageTitle = ({ title, subtitle, children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    const { environment } = useEnvironment();
    return (_jsxs(Box, Object.assign({ id: "account-page-title", height: "100%", width: "100%" }, { children: [_jsxs(Stack, Object.assign({ flexWrap: "wrap", mb: isMobile ? 4 : 2, rowGap: isMobile ? 2 : 0, flexGrow: 1 }, { children: [_jsxs(Stack, Object.assign({ flexDirection: !isMobile ? "row" : "column", flexWrap: "wrap", justifyContent: "space-between", alignItems: !isMobile ? "center" : "stretch", flexGrow: 1, rowGap: isMobile ? 0 : 2 }, { children: [_jsx(TitleComponent, { title: title, environment: environment }), isMobile ? _jsx(SubtitleSection, Object.assign({}, { subtitle })) : children] })), isMobile ? children : _jsx(SubtitleSection, Object.assign({}, { subtitle }))] })), 
            // Do not show the divider on mobile views.
            !isMobile && (_jsx(Divider, { style: {
                    height: theme.spacing(0.25),
                    marginBottom: theme.spacing(4),
                } }))] })));
};
